import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Boxes, Plus, Save, Trash2, GripVertical } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import schedules from "@/api/schedules";

/**
 * Phase 4.9 — Admin: schedule sections CRUD.
 *
 * Lets admins manage the section pool (color, name, order) used by the
 * weekly Schedule grid. TOP and the customer-default sections are seeded.
 */
export default function ScheduleSections() {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState("#1E293B");

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await schedules.listSections();
      setRows(r.data || []);
    } catch {
      toast.error(t("common.errorLoading"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const update = (id, patch) => {
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  const save = async (row) => {
    try {
      await schedules.patchSection(row.id, {
        name: row.name,
        color: row.color,
        order: row.order,
      });
      toast.success(t("scheduleSections.saved"));
      refresh();
    } catch {
      toast.error(t("common.errorSaving"));
    }
  };

  const remove = async (row) => {
    if (!window.confirm(t("scheduleSections.confirmDelete", { name: row.name }))) {
      return;
    }
    try {
      await schedules.deleteSection(row.id);
      toast.success(t("scheduleSections.deleted"));
      refresh();
    } catch {
      toast.error(t("common.errorDeleting"));
    }
  };

  const create = async () => {
    if (!newName.trim()) return;
    try {
      await schedules.createSection({
        name: newName,
        color: newColor,
        order: rows.length,
      });
      setNewName("");
      setNewColor("#1E293B");
      toast.success(t("scheduleSections.created"));
      refresh();
    } catch {
      toast.error(t("common.errorSaving"));
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
          <Boxes className="h-6 w-6 text-primary" />
          {t("scheduleSections.title")}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("scheduleSections.description")}
        </p>
      </div>

      {/* Create */}
      <Card data-testid="sections-create-card">
        <CardHeader>
          <CardTitle className="text-base">
            {t("scheduleSections.createTitle")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 space-y-1">
              <Label className="text-xs">{t("scheduleSections.name")}</Label>
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="OPERATIONS & LOGISTICS"
                className="h-9 text-xs uppercase"
                data-testid="sections-new-name"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">{t("scheduleSections.color")}</Label>
              <Input
                type="color"
                value={newColor}
                onChange={(e) => setNewColor(e.target.value)}
                className="h-9 w-16 cursor-pointer p-1"
                data-testid="sections-new-color"
              />
            </div>
            <Button
              onClick={create}
              disabled={!newName.trim()}
              data-testid="sections-create-submit"
            >
              <Plus className="mr-1 h-4 w-4" />
              {t("scheduleSections.create")}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {t("scheduleSections.listTitle", { n: rows.length })}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-6 text-center text-xs text-muted-foreground">
              {t("common.loading")}
            </div>
          ) : (
            <div className="space-y-2">
              {rows.map((row) => (
                <div
                  key={row.id}
                  className="flex items-center gap-2 rounded-md border border-border bg-card p-2"
                  data-testid={`sections-row-${row.id}`}
                >
                  <GripVertical className="h-4 w-4 text-muted-foreground" />
                  <Input
                    type="number"
                    value={row.order}
                    onChange={(e) =>
                      update(row.id, { order: parseInt(e.target.value, 10) || 0 })
                    }
                    className="h-8 w-16 text-xs"
                  />
                  <Input
                    type="color"
                    value={row.color || "#1E293B"}
                    onChange={(e) => update(row.id, { color: e.target.value })}
                    className="h-8 w-12 cursor-pointer p-1"
                  />
                  <Input
                    value={row.name}
                    onChange={(e) => update(row.id, { name: e.target.value })}
                    className="h-8 flex-1 text-xs uppercase"
                  />
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => save(row)}
                    className="h-8"
                  >
                    <Save className="h-3 w-3" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => remove(row)}
                    className="h-8 text-destructive hover:bg-destructive/10"
                    disabled={row.name === "TOP"}
                    title={row.name === "TOP" ? t("scheduleSections.cannotDeleteTop") : ""}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
